import { Vector3 } from 'three';
import { Element } from '@thive/canvas';
import { LoadType, RackingArea } from '@thive/fddm-types';

import { calcRackShelfHeights } from '@/modules/common/helpers/rack';
import { NumUtils } from '@/modules/common/utils';
import { FDDM_TAG } from '@/modules/floorplanDesign/constants';
import { AREAS_COLORS_MAP, modifyColor } from '@/modules/floorplanDesign/mappers/colors';

import { Z_INDEX_MAP } from '../';
import { elementFactory } from '../factories';
import { createModelAreaRectangleElement } from './modelMapperHelpers';

const getMaximumLoadCarrierHeight = (loadTypes: LoadType[]): number =>
  loadTypes.reduce((maximumHeight, loadType) => Math.max(maximumHeight, loadType.height), 0);

const createBaseRack = (rack: RackingArea, allLoadTypes: LoadType[]) => {
  const { margin, rackSpec, rectangle } = rack;
  const { bayLength, bayDepth, beamHeight, beamLength, clearanceTop, shelfHeights, loadTypes } =
    rackSpec;

  // load carrier bounding box
  const supportedLoadTypeIds = new Set(loadTypes.map((loadType) => loadType.loadTypeId));
  const supportedLoadCarrierTypes = allLoadTypes.filter((loadType) =>
    supportedLoadTypeIds.has(loadType.id),
  );
  const loadCarrierHeight = getMaximumLoadCarrierHeight(supportedLoadCarrierTypes);

  // height of each shelve
  // Copy before removing the floor level so mapping never mutates the FDDM source data.
  const levelHeight = loadCarrierHeight + clearanceTop;
  const levelHeights =
    shelfHeights.length > 0
      ? [...shelfHeights]
      : calcRackShelfHeights(shelfHeights[0], levelHeight, shelfHeights.length, beamHeight);
  if (levelHeights[0] === 0) levelHeights.shift();

  // derived rack dimensions
  const postWidth = NumUtils.mmToMeters(
    (NumUtils.metersToMmRounded(bayLength) - NumUtils.metersToMmRounded(beamLength)) / 2,
  );

  // Derive amount of 3D bays based on available space and rack dimensions
  const maxAvailableRackingWidth = rectangle.length - margin * 2;
  let numBaysFittingInArea = 0;
  /* Note:
    we start with 0 and not 1* <postWidth> because of the way the 3d canvas draws its racks 
    (with the posts their center at the outer edge of the correct dimensions, and not the posts their actual outer edge)
  */
  let occupiedWidth = 0;
  while (occupiedWidth + beamLength + postWidth <= maxAvailableRackingWidth) {
    occupiedWidth += beamLength + postWidth;
    numBaysFittingInArea++;
  }
  if (numBaysFittingInArea === 0) numBaysFittingInArea = 1;

  const totalRackingWidth = numBaysFittingInArea * (beamLength + postWidth); // center to center between outer posts
  const totalRackingDepth = bayDepth;

  return {
    totalRackingWidth,
    totalRackingDepth,
    levelHeights,
    shelfLength: NumUtils.mmToMeters(
      NumUtils.metersToMmRounded(totalRackingWidth) / numBaysFittingInArea,
    ),
    postWidth,
  };
};

const createRackAreaElement = (rack: RackingArea): Element =>
  createModelAreaRectangleElement(
    `${rack.id}-rect`,
    rack.rectangle,
    Z_INDEX_MAP[FDDM_TAG.RACKING_AREA],
    modifyColor(AREAS_COLORS_MAP[rack.rackingLoadHandlingType], true),
  );

const createTwoSidedRack = (rack: RackingArea, allLoadTypes: LoadType[]): Element[] => {
  const { rectangle, stacking, alongsideExtension } = rack;
  const { aisleWidth } = alongsideExtension ?? { aisleWidth: 1 };
  const { totalRackingWidth, totalRackingDepth, levelHeights, shelfLength, postWidth } =
    createBaseRack(rack, allLoadTypes);

  // alignment factor
  let alignmentFactor: number;
  if (stacking === 'HORIZONTAL_TOP') {
    alignmentFactor = -1;
  } else if (stacking === 'HORIZONTAL_MIDDLE' || stacking === 'VERTICAL_MIDDLE') {
    alignmentFactor = 0;
  } else {
    alignmentFactor = 1;
  }

  // position
  const xOffset = -rectangle.length / 2; // center in X
  const yOffset =
    -rectangle.width / 2 +
    alignmentFactor * (rectangle.length / 2 - totalRackingWidth - aisleWidth / 2);
  const deltaLength = rectangle.length - totalRackingWidth;
  const position: Vector3 = new Vector3(
    deltaLength / 2 + xOffset,
    rectangle.width / 2 + yOffset,
    0,
  );

  const name = `${rack.name || rack.id}-rack`;

  return [
    createRackAreaElement(rack),
    elementFactory.buildRackElement({
      id: `${rack.id}-rack-0`,
      name: `${name}-0`,
      position: position.clone().add(new Vector3(totalRackingWidth, -aisleWidth / 2, 0)),
      rotation: new Vector3(0, 0, 180),
      tags: [FDDM_TAG.MODEL],
      frameLength: totalRackingWidth,
      frameDepth: totalRackingDepth,
      shelfHeights: levelHeights,
      shelfLength,
      parentId: `${rack.id}-rect`, // attach to 3D rack rect for rotation
      bumpers: false,
      horizontalBeams: true,
      postSize: postWidth,
    }),
    elementFactory.buildRackElement({
      id: `${rack.id}-rack-1`,
      parentId: `${rack.id}-rect`, // attach to 3D rack rect for rotation
      name: `${name}-1`,
      position: position.clone().add(new Vector3(0, aisleWidth / 2, 0)),
      rotation: new Vector3(0, 0, 0),
      tags: [FDDM_TAG.MODEL],
      frameLength: totalRackingWidth,
      frameDepth: totalRackingDepth,
      shelfHeights: levelHeights,
      shelfLength,
      bumpers: false,
      horizontalBeams: true,
      postSize: postWidth,
    }),
  ];
};

const createOneSidedRack = (rack: RackingArea, allLoadTypes: LoadType[]): Element[] => {
  const { rectangle, stacking } = rack;
  const { totalRackingWidth, totalRackingDepth, levelHeights, shelfLength, postWidth } =
    createBaseRack(rack, allLoadTypes);

  // position & rotation are relative to the rect from createArea() above
  let scale: number;
  if (stacking === 'HORIZONTAL_TOP') {
    scale = 1;
  } else if (stacking === 'HORIZONTAL_MIDDLE' || stacking === 'VERTICAL_MIDDLE') {
    scale = 0.5;
  } else {
    scale = 0;
  }
  const xOffset = -totalRackingWidth / 2;
  const yOffset = -rectangle.width / 2 + scale * (rectangle.width - totalRackingDepth);

  const name = `${rack.name || rack.id}-rack`;
  const tags = [FDDM_TAG.MODEL];

  return [
    createRackAreaElement(rack),
    elementFactory.buildRackElement({
      id: `${rack.id}-rack`,
      parentId: `${rack.id}-rect`, // attach to 3D rack rect for rotation
      name,
      position: new Vector3(xOffset, yOffset, 0),
      rotation: new Vector3(0, 0, 0),
      tags,
      frameLength: totalRackingWidth,
      frameDepth: totalRackingDepth,
      shelfHeights: levelHeights,
      shelfLength,
      horizontalBeams: true,
      bumpers: true,
      postSize: postWidth,
    }),
  ];
};

export const createRack = (rack: RackingArea, loadTypes: LoadType[]): Element[] => {
  if (
    rack.rackType === 'ONE_SIDED_INSIDE' ||
    rack.rackType === 'ONE_SIDED_ALONGSIDE' ||
    rack.rackType === 'SHUTTLE'
  ) {
    return createOneSidedRack(rack, loadTypes);
  }

  if (rack.rackType === 'TWO_SIDED_ALONGSIDE') {
    return createTwoSidedRack(rack, loadTypes);
  }

  return [];
};
